import { useMemo } from "react";
import { CourseCombobox } from "@/components/CourseCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bookmark, Plus, Trash2, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtDate, fmtTime } from "@/lib/helpers";

export const newLine = (email = "") => ({ kind: "course", course_id: "", session_id: "", unit_price: "", price_mode: "each", bulk_total: "", cands: [{ name: "", email }] });
export const newCustomLine = () => ({ kind: "custom", description: "", quantity: "1", unit_price: "", price_mode: "each", bulk_total: "", cands: [] });
export const filledCands = (ln) => ln.cands.filter((c) => c.name.trim() || c.email.trim());
export const lineQty = (ln) => (ln.kind === "custom" ? Math.max(1, parseInt(ln.quantity, 10) || 1) : filledCands(ln).length);
export const lineTotal = (ln) => (ln.price_mode === "bulk" ? Number(ln.bulk_total) || 0 : (Number(ln.unit_price) || 0) * lineQty(ln));

const PriceInputs = ({ idx, line, setLine, unitLabel }) => (
  <div className="flex items-end gap-3 flex-wrap">
    <div className="flex rounded-md border border-border overflow-hidden text-xs" data-testid={`order-line-${idx}-price-mode`}>
      {[["each", unitLabel], ["bulk", "Bulk price"]].map(([m, l]) => (
        <button key={m} type="button" onClick={() => setLine({ ...line, price_mode: m })} className={`px-3 py-2 ${line.price_mode === m ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`} data-testid={`order-line-${idx}-mode-${m}`}>{l}</button>
      ))}
    </div>
    {line.price_mode === "bulk" ? (
      <div className="w-56"><Label>Bulk price (£, ex VAT)</Label><Input type="number" min="0" step="0.01" value={line.bulk_total} onChange={(e) => setLine({ ...line, bulk_total: e.target.value })} className="mt-1.5 bg-background" data-testid={`order-line-${idx}-bulk`} /></div>
    ) : (
      <div className="w-56"><Label>{unitLabel} (£, ex VAT)</Label><Input type="number" min="0" step="0.01" value={line.unit_price} onChange={(e) => setLine({ ...line, unit_price: e.target.value })} className="mt-1.5 bg-background" data-testid={`order-line-${idx}-price`} /></div>
    )}
  </div>
);

export const CustomLine = ({ idx, line, setLine, onRemove, onSaveItem }) => (
  <div className="rounded-lg border border-dashed border-border p-4 space-y-3 bg-muted/10" data-testid={`order-line-${idx}`}>
    <div className="flex items-center justify-between">
      <p className="font-medium text-sm">Custom line</p>
      {onRemove && <Button variant="ghost" size="sm" onClick={onRemove} className="text-muted-foreground hover:text-red-600" data-testid={`order-line-${idx}-remove`}><Trash2 className="h-4 w-4 mr-1" />Remove line</Button>}
    </div>
    <div className="grid grid-cols-[1fr_90px] gap-3">
      <div><Label>Description</Label><Input value={line.description} onChange={(e) => setLine({ ...line, description: e.target.value })} placeholder="e.g. In-house training day, travel, consultancy" className="mt-1.5 bg-background" data-testid={`order-line-${idx}-description`} /></div>
      <div><Label>Qty</Label><Input type="number" min="1" value={line.quantity} onChange={(e) => setLine({ ...line, quantity: e.target.value })} className="mt-1.5 bg-background" data-testid={`order-line-${idx}-qty`} /></div>
    </div>
    <div className="flex items-end justify-between gap-3 flex-wrap">
      <PriceInputs idx={idx} line={line} setLine={setLine} unitLabel="Unit price" />
      {onSaveItem && <Button variant="ghost" size="sm" onClick={onSaveItem} data-testid={`order-line-${idx}-save-item`}><Bookmark className="h-4 w-4 mr-1" />Save for next time</Button>}
    </div>
  </div>
);

export const SavedItemsMenu = ({ items, onPick, onDelete }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" disabled={!items.length} data-testid="order-saved-items-button"><Bookmark className="h-4 w-4 mr-1.5" />Saved items{items.length ? ` (${items.length})` : ""}</Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-80">
      {items.map((it) => (
        <DropdownMenuItem key={it.id} onSelect={() => onPick(it)} className="flex items-center justify-between gap-2" data-testid={`saved-item-${it.id}`}>
          <span className="truncate">{it.description}</span>
          <span className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
            {it.price_mode === "bulk" ? `£${it.price} bulk` : `${it.quantity} × £${it.price}`}
            <button type="button" title="Delete saved item" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(it); }} className="hover:text-red-600" data-testid={`saved-item-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const OrderCandidates = ({ cands, setCands, idx }) => {
  const update = (i, patch) => setCands(cands.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return (
    <div className="space-y-2">
      <Label>Candidates</Label>
      {cands.map((c, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
          <Input value={c.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Full name" data-testid={`order-line-${idx}-candidate-name-${i}`} />
          <Input type="email" value={c.email} onChange={(e) => update(i, { email: e.target.value })} placeholder="Email (can be shared)" data-testid={`order-line-${idx}-candidate-email-${i}`} />
          <Button variant="ghost" size="icon" disabled={cands.length === 1} onClick={() => setCands(cands.filter((_, j) => j !== i))} data-testid={`order-line-${idx}-candidate-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setCands([...cands, { name: "", email: cands[cands.length - 1]?.email || "" }])} data-testid={`order-line-${idx}-add-candidate`}><Plus className="h-4 w-4 mr-1" />Add another candidate</Button>
    </div>
  );
};

export const OrderLine = ({ idx, line, setLine, onRemove, courses, sessions }) => {
  const course = courses.find((x) => x.id === line.course_id);
  const isLive = course?.course_type === "live";
  const courseSessions = useMemo(() => {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    return sessions.filter((s) => s.course_id === line.course_id && new Date(s.start) >= today);
  }, [sessions, line.course_id]);
  const pickCourse = (id) => {
    const c = courses.find((x) => x.id === id);
    setLine({ ...line, course_id: id, session_id: "", unit_price: c?.price ? String(c.price) : line.unit_price });
  };
  return (
    <div className="rounded-lg border border-border p-4 space-y-3 bg-muted/20" data-testid={`order-line-${idx}`}>
      <div className="flex items-center justify-between">
        <p className="font-medium text-sm">Course {idx + 1}</p>
        {onRemove && <Button variant="ghost" size="sm" onClick={onRemove} className="text-muted-foreground hover:text-red-600" data-testid={`order-line-${idx}-remove`}><Trash2 className="h-4 w-4 mr-1" />Remove course</Button>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Course</Label><CourseCombobox courses={courses} value={line.course_id} onChange={pickCourse} testid={`order-line-${idx}-course`} /></div>
        <div>
          <Label>Session</Label>
          {line.course_id && !isLive ? (
            <p className="mt-1.5 h-9 flex items-center text-sm text-muted-foreground" data-testid={`order-line-${idx}-no-session`}>Not needed: e-learning / online course</p>
          ) : (
            <Select value={line.session_id} onValueChange={(v) => setLine({ ...line, session_id: v })} disabled={!line.course_id}>
              <SelectTrigger className="mt-1.5 bg-background" data-testid={`order-line-${idx}-session`}><SelectValue placeholder={line.course_id && courseSessions.length === 0 ? "No upcoming sessions" : "Select a session"} /></SelectTrigger>
              <SelectContent>{courseSessions.map((s) => <SelectItem key={s.id} value={s.id}>{fmtDate(s.start)} {fmtTime(s.start)} · {s.location} ({s.seats_left} seats left)</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>
      <PriceInputs idx={idx} line={line} setLine={setLine} unitLabel="Price per candidate" />
      <OrderCandidates idx={idx} cands={line.cands} setCands={(cands) => setLine({ ...line, cands })} />
    </div>
  );
};
